import { useMemo } from 'react';
import { useMetricsTableStyles } from './MetricsTable.styles';
import {
    CaretDownFilled,
    CaretUpFilled,
    CircleFilled,
    SubtractCircleRegular,
} from '@fluentui/react-icons';
import { mergeClasses, Text } from '@fluentui/react-components';

export interface IMetricsTableRow {
    key: string;
    label?: string;
    value: number;
    trend?: number;
    legendColor?: string;
}

export interface IMetricsTableProps {
    keyLabel: string;
    valueLabel: string;
    rows: IMetricsTableRow[];
    isPreviousRange?: boolean;
    onRowClick?: (row: IMetricsTableRow) => void;
    className?: string;
}

const TableRow: React.FC<{
    row: IMetricsTableRow;
    idx: number;
    rangeClassName: string;
    onClick?: () => void;
}> = ({ row, idx, rangeClassName, onClick }) => {
    const styles = useMetricsTableStyles();

    const trendDisplay = useMemo(() => {
        const trend = row.trend;

        if (trend === undefined || Number.isNaN(trend)) {
            return undefined;
        }

        if (trend === 0) {
            return (
                <span className={styles.trendValue}>
                    <SubtractCircleRegular />
                    {trend}
                </span>
            );
        }

        return trend > 0 ? (
            <span className={mergeClasses(styles.trendValue, 'up')}>
                <CaretUpFilled />
                {trend}
            </span>
        ) : (
            <span className={mergeClasses(styles.trendValue, 'down')}>
                <CaretDownFilled />
                {Math.abs(trend)}
            </span>
        );
    }, [row.trend, styles]);

    return (
        <div
            className={mergeClasses(
                styles.row,
                rangeClassName,
                idx % 2 === 0 ? 'even' : 'odd',
                onClick ? 'hoverable' : ''
            )}
            onClick={() => {
                if (onClick) {
                    onClick();
                }
            }}
        >
            <div className={mergeClasses(styles.cell, styles.keyCell)}>
                <span>
                    {row.legendColor && (
                        <CircleFilled color={row.legendColor} />
                    )}
                    {row.label ?? row.key}
                </span>
            </div>
            <div className={styles.cell}>
                <span>{row.value}</span>
            </div>
            <div className={mergeClasses(styles.cell, styles.trendCell)}>
                <span>{trendDisplay}</span>
            </div>
        </div>
    );
};

const MetricsTable: React.FC<IMetricsTableProps> = ({
    keyLabel,
    valueLabel,
    rows,
    isPreviousRange,
    onRowClick,
    className,
}) => {
    const styles = useMetricsTableStyles();

    const rangeClassName = useMemo(() => {
        return isPreviousRange
            ? styles.previousRangeRow
            : styles.currentRangeRow;
    }, [styles, isPreviousRange]);

    return (
        <div className={mergeClasses(styles.root, className)}>
            <div className={styles.header}>
                <Text weight="semibold">{keyLabel}</Text>
                <Text weight="semibold">{valueLabel}</Text>
            </div>
            <div className={styles.tableBody}>
                {rows.map((row, i) => (
                    <TableRow
                        key={row.key}
                        row={row}
                        idx={i}
                        rangeClassName={rangeClassName}
                        onClick={onRowClick ? () => onRowClick(row) : undefined}
                    />
                ))}
            </div>
        </div>
    );
};

export default MetricsTable;
